import { get } from 'lodash';

import { config } from 'api';
import { setErrorMessage, setSuccessMessage } from 'apps/notifications/actions/notifications';
import { UNKNOWN_ERROR } from 'utils/errors';
import {
  reportBasketAddedToExistingShoppingList,
  reportBasketAddedToNewShoppingList,
  reportItemAddedToExistingShoppingList,
  reportItemAddedToNewShoppingList,
  reportShoppingListCreated,
} from '../analytics';
import exceptionCodesMessages from '../exceptionCodesMessages';

export const OPEN_SHOPPING_LISTS_MODAL = 'OPEN_SHOPPING_LISTS_MODAL';
export const CLOSE_SHOPPING_LISTS_MODAL = 'CLOSE_SHOPPING_LISTS_MODAL';
export const OPEN_SHOPPING_LISTS_FORM = 'OPEN_SHOPPING_LISTS_FORM';
export const OPEN_SHOPPING_LISTS_MODAL_FROM_BASKET = 'OPEN_SHOPPING_LISTS_MODAL_FROM_BASKET';
export const CLOSE_SHOPPING_LISTS_FORM = 'CLOSE_SHOPPING_LISTS_FORM';
export const ADD_SHOPPING_LIST = 'ADD_SHOPPING_LIST';
export const ADD_TO_SHOPPING_LIST = 'ADD_TO_SHOPPING_LIST';
export const REMOVE_FROM_SHOPPING_LIST = 'REMOVE_FROM_SHOPPING_LIST';
export const REQUEST_SHOPPING_LISTS = 'REQUEST_SHOPPING_LISTS';
export const RECEIVE_SHOPPING_LISTS = 'RECEIVE_SHOPPING_LISTS';
export const UPDATE_NEW_SHOPPING_LIST_FIELD = 'UPDATE_NEW_SHOPPING_LIST_FIELD';

function getErrorMessage(err) {
  const errorCode = get(err, 'data.code');
  if (errorCode && exceptionCodesMessages[errorCode]) {
    return exceptionCodesMessages[errorCode];
  }
  return UNKNOWN_ERROR.error;
}

function requestShoppingLists() {
  return {
    type: REQUEST_SHOPPING_LISTS,
  };
}

function receiveShoppingLists(items) {
  return {
    type: RECEIVE_SHOPPING_LISTS,
    items,
  };
}

function addSkuToShoppingList(shoppingListId) {
  return {
    type: ADD_TO_SHOPPING_LIST,
    shoppingListId,
  };
}

function removeSkuFromShoppingList(shoppingListId) {
  return {
    type: REMOVE_FROM_SHOPPING_LIST,
    shoppingListId,
  };
}

function addShoppingList(id, name) {
  return {
    type: ADD_SHOPPING_LIST,
    id,
    name,
  };
}

export function clearNewShoppingListField() {
  return {
    type: UPDATE_NEW_SHOPPING_LIST_FIELD,
    newShoppingList: '',
  };
}

export function updateNewShoppingListField(newShoppingList) {
  return {
    type: UPDATE_NEW_SHOPPING_LIST_FIELD,
    newShoppingList,
  };
}

export function addToShoppingList(shoppingListId, sku) {
  return (dispatch, store, { network }) => {
    dispatch(addSkuToShoppingList(shoppingListId));
    return network
      .patch(`${config.apiBasePath}/shoppinglists/${shoppingListId}`, {
        body: JSON.stringify({
          sku,
          quantityInList: 1,
        }),
      })
      .then(() => {
        reportItemAddedToExistingShoppingList(sku);
        dispatch(setSuccessMessage('Item added successfully'));
      })
      .catch(err => {
        dispatch(removeSkuFromShoppingList(shoppingListId));
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function addToShoppingListFromBasket(shoppingListId, basket) {
  return (dispatch, store, { network }) => {
    dispatch(addSkuToShoppingList(shoppingListId));
    return network
      .patch(`${config.apiHost}/webshop/webservices/shoppingLists/${shoppingListId}/mergeWithBasket`)
      .then(() => {
        reportBasketAddedToExistingShoppingList(basket);
        dispatch(setSuccessMessage('Items added successfully'));
      })
      .catch(err => {
        dispatch(removeSkuFromShoppingList(shoppingListId));
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function loadShoppingListsForSku(sku) {
  return (dispatch, store, { network }) => {
    dispatch(requestShoppingLists());
    return network
      .get(`${config.apiBasePath}/shoppinglists?checkSku=${sku}`)
      .then((data) => {
        dispatch(receiveShoppingLists(data));
      })
      .catch(err => {
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function loadAllShoppingLists() {
  return (dispatch, store, { network }) => {
    dispatch(requestShoppingLists());
    return network
      .get(`${config.apiHost}/webshop/webservices/shoppingLists/withoutItems/`)
      .then((data) => {
        dispatch(receiveShoppingLists(data));
      })
      .catch(err => {
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function createShoppingList(shoppingListName, sku) {
  return (dispatch, store, { network }) => {
    if (!shoppingListName) {
      return dispatch(setErrorMessage('Please give your list a name.'));
    }

    return network
      .post(`${config.apiBasePath}/shoppinglists`, {
        body: JSON.stringify({
          name: shoppingListName,
          shoppingListItems: [{
            sku,
            quantityInList: 1,
          }],
        }),
      })
      .then(shoppingListId => {
        reportShoppingListCreated();
        reportItemAddedToNewShoppingList(sku);
        dispatch(setSuccessMessage('Item added successfully'));
        dispatch(addShoppingList(shoppingListId, shoppingListName));
        dispatch(addSkuToShoppingList(shoppingListId));
        dispatch(clearNewShoppingListField());
      })
      .catch(err => {
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function createShoppingListFromBasket(shoppingListName, basket) {
  return (dispatch, store, { network }) => {
    if (!shoppingListName) {
      return dispatch(setErrorMessage('Please give your list a name.'));
    }
    return network
      .post(`${config.apiHost}/webshop/webservices/shoppingLists/createFromBasket`, {
        body: JSON.stringify({
          name: shoppingListName,
        }),
      })
      .then(({ id: shoppingListId }) => {
        reportShoppingListCreated();
        reportBasketAddedToNewShoppingList(basket);
        dispatch(setSuccessMessage('Items added successfully'));
        dispatch(addShoppingList(shoppingListId, shoppingListName));
        dispatch(addSkuToShoppingList(shoppingListId));
        dispatch(clearNewShoppingListField());
      })
      .catch(err => {
        dispatch(setErrorMessage(getErrorMessage(err)));
      });
  };
}

export function openShoppingListsModal(product) {
  return (dispatch, getState, { loginWatcher }) =>
    loginWatcher.subscribe(getState)
      .then(() => dispatch({
        type: OPEN_SHOPPING_LISTS_MODAL,
        product,
      }));
}

export function openShoppingListsModalFromBasket() {
  return (dispatch, getState, { loginWatcher }) =>
    loginWatcher.subscribe(getState)
      .then(() => dispatch({
        type: OPEN_SHOPPING_LISTS_MODAL_FROM_BASKET,
      }));
}

export function closeShoppingListsModal() {
  return {
    type: CLOSE_SHOPPING_LISTS_MODAL,
  };
}

export function openForm() {
  return {
    type: OPEN_SHOPPING_LISTS_FORM,
  };
}

export function closeForm() {
  return {
    type: CLOSE_SHOPPING_LISTS_FORM,
  };
}
